// One way to declare a server read.
//
// A resource is defined ONCE and consumed from anywhere: a hook does
// `useQuery(productionsList(q))`, a route loader does
// `warm(queryClient, productionsList(q))`, and an invalidation does
// `queryClient.invalidateQueries({ queryKey: productionsList.all })`. All three
// share one cache slot because all three come from the same declaration.
//
// This generalises the shape already proven in this repo at
// `hooks.ts`'s `copilotStatusQueryOptions`, which exists precisely because that
// one query needed to be read from both a component and a route guard. It was
// the only `queryOptions()` in 81 call sites; every other hook re-typed its key,
// its fetch and its cache policy inline.
//
// WHAT THE NAMESPACE MEANS
// ------------------------
// The namespace owns transport policy (GC, retry and polling defaults). Data
// freshness is now a separate, explicit contract: every resource either names
// the server revision domains that make it stale, declares itself volatile, or
// declares itself immutable. Domain-backed reads stay fresh until the revision
// coordinator invalidates them; no response is persisted in the browser.

import { keepPreviousData, queryOptions } from '@tanstack/react-query';
import type { DataDomain } from '@/api/types';
export type { DataDomain } from '@/api/types';

// ARCHITECTURE.md §4: realtime reads back off 5s → 10s → 20s → 30s on failure.
// Lives here rather than in `queries.ts` because it is a policy value, and
// `POLICY.realtime` is the only thing that should ever read it — sixteen hooks
// used to write `retryDelay: (n) => withRealtimeBackoff(n)` by hand.
const REALTIME_DELAYS_MS = [5000, 10000, 20000, 30000];

export function withRealtimeBackoff(failureCount: number): number {
  return REALTIME_DELAYS_MS[Math.min(failureCount, REALTIME_DELAYS_MS.length - 1)]!;
}

/**
 * staleTime tiers. The only place a freshness duration is written.
 *
 * Fifteen distinct spellings were in use for about six intended values —
 * `60_000` and `60000`, `15 * 60_000` and `15 * 60 * 1000` — which made it
 * impossible to tell an intentional difference from a typo.
 */
export const FRESHNESS = {
  /** Always refetch on mount. For reads that must observe another actor. */
  live: 0,
  /** Matches the route-intent manifest deduplication window. */
  intent: 5_000,
  seconds: 15_000,
  minute: 60_000,
  fiveMinutes: 5 * 60_000,
  quarterHour: 15 * 60_000,
  hour: 60 * 60_000,
  /** Content-addressed or otherwise immutable; refetching cannot change it. */
  immutable: Number.POSITIVE_INFINITY,
} as const;

/** refetchInterval cadences. The only place a poll interval is written. */
export const CADENCE = {
  /**
   * An operation the user is watching happen. Admin Config's apply is a
   * multi-second server round trip; at 5s it reads as hung.
   */
  urgent: 2_000,
  fast: 5_000,
  snapshot: 10_000,
  /**
   * Deliberately not `slow`: Admin Config polls this fast when idle so the
   * concurrent-edit banner can catch a second admin mid-edit.
   */
  idle: 20_000,
  slow: 30_000,
  ambient: 60_000,
  lazy: 300_000,
} as const;

export type Namespace =
  | 'session'
  | 'authoritative'
  | 'derived'
  | 'reporting'
  | 'private'
  | 'realtime'
  | 'public';

interface NamespacePolicy {
  staleTime: number;
  gcTime: number;
  retry: number | boolean;
  refetchOnWindowFocus: boolean;
  refetchOnReconnect: boolean;
  retryDelay?: (failureCount: number) => number;
  /** Removed by `clearPrivateQueryCache` on a 401 or an identity change. */
  purgeOnLogout: boolean;
}

const ONE_DAY = 24 * 60 * 60 * 1000;
const FIVE_MIN = 5 * 60_000;

export const POLICY: Readonly<Record<Namespace, NamespacePolicy>> = {
  // Auth state. Byte-identical to what useMe, requireSession and login.tsx each
  // wrote separately.
  session: {
    staleTime: FRESHNESS.immutable,
    gcTime: FRESHNESS.immutable,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    purgeOnLogout: true,
  },
  // Long-lived reads. Byte-identical to the QueryClient defaults today, which is
  // what makes migrating a hook onto the factory a provable no-op.
  authoritative: {
    staleTime: FRESHNESS.quarterHour,
    gcTime: ONE_DAY,
    retry: 1,
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
    purgeOnLogout: true,
  },
  // Computed projections. They are still server-owned and revision coherent,
  // but their potentially large/transient payloads leave memory sooner.
  derived: {
    staleTime: FRESHNESS.minute,
    gcTime: FIVE_MIN,
    retry: 1,
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
    purgeOnLogout: true,
  },
  // Operator reporting reads are mutable snapshots, not catalog data. Keep a
  // short in-memory cache so tab/filter changes do not churn the API and
  // refresh it when an operator returns to the window.
  reporting: {
    staleTime: FRESHNESS.minute,
    gcTime: FIVE_MIN,
    retry: 1,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    purgeOnLogout: true,
  },
  // Authenticated reads whose payload carries PII. Nothing is persisted, but a
  // distinct namespace keeps their short lifetime and logout treatment
  // explicit instead of relying on ad-hoc keys.
  private: {
    staleTime: FRESHNESS.minute,
    gcTime: FIVE_MIN,
    retry: 1,
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
    purgeOnLogout: true,
  },
  realtime: {
    staleTime: FRESHNESS.live,
    gcTime: FIVE_MIN,
    retry: 1,
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
    retryDelay: withRealtimeBackoff,
    purgeOnLogout: true,
  },
  // Token-gated screens that belong to nobody. These MUST survive a private
  // cache purge: a logged-out reviewer's `GET /me` 401s by definition, and a
  // purge that took their in-flight page query with it left the query aborted
  // and pending forever. `isPublicKey` has tested for this namespace since that
  // incident — but the namespace itself was never declared, so the only two
  // public keys were hand-typed strings that nothing checked.
  public: {
    staleTime: FRESHNESS.minute,
    gcTime: FIVE_MIN,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
    purgeOnLogout: false,
  },
};

/** Server-owned revision domains returned by `GET /data-revisions`. */
export const DATA_DOMAINS = [
  'identity_rbac',
  'catalog',
  'campaigns',
  'audiences',
  'creatives',
  'pixels',
  'configuration',
  'delivery_reporting',
  'attribution_reporting',
] as const satisfies readonly DataDomain[];

export type ResourceCoherency =
  | {
    domains: readonly DataDomain[];
    volatile?: never;
    immutable?: never;
    /** Domain revision is the only freshness clock. */
    freshness?: never;
  }
  | { domains?: never; volatile: true; immutable?: never }
  | {
    domains?: never;
    volatile?: never;
    immutable: true;
    freshness?: never;
  };

export interface ResourceQueryMeta {
  coherency: ResourceCoherency;
}

/** A poll interval, or a function of the last successful payload. */
export type Cadence<TRaw> = number | false | ((raw: TRaw | undefined) => number | false);

interface QuerySpecBase<TInput, TRaw, TData = TRaw> {
  namespace: Namespace;
  /** Second key segment, e.g. `productions.list`. Unique across the registry. */
  name: string;
  /** The key TAIL — everything after `[namespace, name]`. Defaults to `[input]`. */
  keyOf?: (input: TInput) => readonly unknown[];
  /** The request. `signal` comes from TanStack, so every fetch is cancellable. */
  request: (input: TInput, signal: AbortSignal) => Promise<TRaw>;
  /**
   * Strips the response envelope. Declared once here rather than as a `select`
   * at each call site, so the factory owns HOW it is applied.
   */
  unwrap?: (raw: TRaw) => TData;
  /** Overrides the namespace staleTime. Every use needs a reason at the definition. */
  freshness?: number;
  cadence?: Cadence<TRaw>;
  refetchOnWindowFocus?: boolean;
  refetchOnReconnect?: boolean;
  retry?: number | boolean;
  /**
   * This payload carries personal data.
   *
   * Declared rather than inferred, because nothing about a response shape says
   * "reviewer email" — `creatives.reviewPackages` looks exactly like any other
   * list until you read `approvals[]`. No query responses are persisted; this
   * marker remains a registry-audit aid for particularly sensitive payloads.
   */
  pii?: true;
  /**
   * Hold the previous result while a new key loads, instead of dropping to
   * undefined. Declared as intent rather than as a raw `placeholderData`
   * callback: the only thing anyone has ever wanted here is "do not flash
   * empty when the filters change", and spelling it out invites the callback
   * to grow a second meaning.
   */
  keepPreviousData?: boolean;
}

export type QuerySpec<TInput, TRaw, TData = TRaw> = QuerySpecBase<TInput, TRaw, TData>
  & ResourceCoherency;

/** The only knobs a call site may turn. Deliberately tiny. */
export interface CallOverrides<TRaw> {
  /** Gate a query that must not fire yet: a missing id, a denied capability. */
  enabled?: boolean;
  /** An ambient consumer may poll slower than the mounted page, or not at all. */
  cadence?: Cadence<TRaw>;
}

/**
 * The longest delay a browser timer honours (2^31 − 1 ms, about 24.8 days). A longer one
 * fires at once, so a cadence computed from a server timestamp (`refresh_after − now`) on a
 * client whose clock runs weeks behind became a refetch every few milliseconds. The phone
 * suite's frozen clock (2026-09-10 against a real `refresh_after`) found it: `/meta-previews`
 * refetched every ~90 ms and a review tap never landed.
 */
export const MAX_TIMER_MS = 2_147_483_647;

/** A computed cadence, clamped to what a timer can wait. */
export function boundedInterval(interval: number | false | undefined): number | false | undefined {
  return typeof interval === 'number' ? Math.min(interval, MAX_TIMER_MS) : interval;
}

export function defineQuery<TInput, TRaw, TData = TRaw>(spec: QuerySpec<TInput, TRaw, TData>) {
  const policy = POLICY[spec.namespace];
  const prefix = [spec.namespace, spec.name] as const;

  const key = (input: TInput): readonly unknown[] => [
    ...prefix,
    ...(spec.keyOf ? spec.keyOf(input) : [input]),
  ];

  const options = (input: TInput, overrides: CallOverrides<TRaw> = {}) => {
    const cadence = overrides.cadence ?? spec.cadence;
    const revisionControlled = spec.domains !== undefined || spec.immutable === true;
    const coherency: ResourceCoherency = spec.domains !== undefined
      ? { domains: spec.domains }
      : spec.immutable === true
        ? { immutable: true }
        : { volatile: true };
    return queryOptions({
      queryKey: key(input),
      queryFn: ({ signal }) => spec.request(input, signal),
      select: spec.unwrap,
      // A domain revision is the freshness clock. Time-based staleness would
      // let related resources refetch at different moments and produce a torn
      // screen. Volatile reads retain their existing namespace cadence.
      staleTime: revisionControlled ? FRESHNESS.immutable : spec.freshness ?? policy.staleTime,
      gcTime: policy.gcTime,
      retry: spec.retry ?? policy.retry,
      retryDelay: policy.retryDelay,
      refetchOnWindowFocus: spec.refetchOnWindowFocus ?? policy.refetchOnWindowFocus,
      refetchOnReconnect: spec.refetchOnReconnect ?? policy.refetchOnReconnect,
      // Already the v5 default; stated so the architecture doc's claim is true
      // in code rather than by omission.
      refetchIntervalInBackground: false,
      refetchInterval:
        cadence === undefined
          ? undefined
          : typeof cadence === 'function'
            ? (query: { state: { data: TRaw | undefined } }) => boundedInterval(cadence(query.state.data))
            : cadence,
      placeholderData: spec.keepPreviousData ? keepPreviousData : undefined,
      enabled: overrides.enabled,
      meta: { coherency } satisfies ResourceQueryMeta,
    });
  };

  return Object.assign(options, {
    key,
    /**
     * The invalidation prefix. Twenty-three call sites spell a namespace and a
     * name by hand purely to get one of these, so renaming a key silently turns
     * every one of them into a no-op: no type error, no failing test, the UI
     * just stops refreshing after a write.
     */
    all: prefix,
    /**
     * The declaration itself, for registry introspection by the taxonomy guard.
     *
     * Namespace and name are read through here rather than copied onto the
     * function: `Object.assign`ing a `name` onto a function throws, because
     * `Function.prototype.name` is read-only. One source for both also means
     * they cannot drift from the key this resource actually builds.
     */
    spec: spec as unknown as QuerySpec<unknown, unknown, unknown>,
  });
}

export type QueryResource<TInput, TRaw, TData = TRaw> = ReturnType<
  typeof defineQuery<TInput, TRaw, TData>
>;

/** The namespace a key belongs to, or null if it is outside the taxonomy. */
export function namespaceOf(key: readonly unknown[]): Namespace | null {
  const head = key[0];
  return typeof head === 'string' && head in POLICY ? (head as Namespace) : null;
}

/**
 * Survives a private-cache purge. Queries that carry no session and belong to
 * nobody — the token-gated public screens. A logged-out reviewer's `GET /me`
 * 401s by definition, and a purge that took their in-flight page query with it
 * left the query aborted and pending forever with nothing to re-drive it. That
 * is exactly how the creative-review page hung on "Loading…".
 */
export function isPublicKey(key: readonly unknown[]): boolean {
  const ns = namespaceOf(key);
  return ns !== null && !POLICY[ns].purgeOnLogout;
}

export function resourceCoherencyFromMeta(meta: unknown): ResourceCoherency | null {
  if (!meta || typeof meta !== 'object') return null;
  const coherency = (meta as Partial<ResourceQueryMeta>).coherency;
  if (!coherency || typeof coherency !== 'object') return null;
  if ('domains' in coherency && Array.isArray(coherency.domains)) {
    return { domains: coherency.domains };
  }
  if ('volatile' in coherency && coherency.volatile === true) return { volatile: true };
  if ('immutable' in coherency && coherency.immutable === true) return { immutable: true };
  return null;
}
